import api, {type ApiResponse, doError} from "@/axios.js";
import {defineStore} from 'pinia'
import type {AxiosError} from "axios";
import { unregisterPushNotifications } from '@/services/push';
import {resetSessionData} from './session';
let meFlight: Promise<void>|null=null;
let checkedAt=0;


export interface UserInterface extends RegisterInterface {
    name: string;
    id: number;
    first_name:string;
    last_name:string;
    created_at: string;
    email_verified_at: string;
    token?: string;
    is_admin?: number;
    meta?: {
        orders?: number;
        last_reservation?: string;
        next_reservation?: string;
    } ;
}
export interface LoginInterface {
    email: string;
    phone:string;
    password: string;
}
export interface RegisterInterface {
    email: string;
    country_code: string;
    phone:string;
    password: string;
    password_confirmation: string;
    first_name: string;
    last_name: string;
    terms?:boolean;
    privacy?:boolean;
}
export interface PasswordReset {
    email: string;
}
export interface PasswordChange extends PasswordReset{
    password: string;
    password_confirmation: string;
    password_reset_token:string;
}
export const useUserStore = defineStore('user', {
    state: () => ({
        _auth: null as UserInterface | null,
        error: null,
        _list: null as UserInterface[] | null,
    }),
    getters: {
        auth: (state) => state._auth,
        list: (state) => state._list,
    },
    actions: {
        clearLocalSession(): void {
            checkedAt = 0;
            this._auth = null;
            this._list = null;
            localStorage.removeItem('token');
            delete api.defaults.headers.Authorization;
            resetSessionData();
        },
        async load(): Promise<void> {
            try {
                const response = await api.get<ApiResponse<UserInterface[]>>('/users');
                this._list = response.data.data;
            }
            catch (err: AxiosError | any) {
                doError(err);
            }
        },
        async login(credentials: LoginInterface):Promise<void> {
            try {
                const response = await api.post<ApiResponse<UserInterface>>('/login', credentials);
                resetSessionData(); checkedAt=Date.now(); this._list=null;
                this._auth = response.data.data;
                const token = response.data.data?.token;
                if (token) {
                    localStorage.setItem('token', token);
                    api.defaults.headers.Authorization = `Bearer ${token}`;
                }
            }
            catch (err: AxiosError | any) {
                doError(err);
            }
        },

        async register(registerForm: RegisterInterface) {
            try {
                const response = await api.post<ApiResponse<UserInterface>>('/register', registerForm);
                resetSessionData(); checkedAt=Date.now(); this._list=null;
                this._auth = response.data.data;

                const token = response.data.data?.token || '';
                if (token) {
                    localStorage.setItem('token', token);
                    api.defaults.headers.Authorization = `Bearer ${token}`;
                }
                return true;
            }
            catch (err: AxiosError | any) {
                doError(err);
            }
        },

        async logout(): Promise<void> {
            try {
                await unregisterPushNotifications(false).catch(() => {});
                await api.post('/logout');
            } catch { /* Local sign-out must also work when the network is unavailable. */ }
            finally {
                this.clearLocalSession();
            }
        },
        async me(force=false): Promise<void> {
            const token=localStorage.getItem('token');
            if (!token) { this._auth=null; delete api.defaults.headers.Authorization; return; }
            if (!force && this._auth && Date.now()-checkedAt<30_000) return;
            if (meFlight) return meFlight;
            meFlight=(async()=>{
                try {
                    const response=await api.get('/me');
                    if (localStorage.getItem('token')!==token) return;
                    this._auth=response.data.data; checkedAt=Date.now();
                } catch (err:any) {
                    if (localStorage.getItem('token')===token && err.response?.status === 401) {
                        this.clearLocalSession();
                    }
                } finally { meFlight=null; }
            })();
            return meFlight;
        },
        async update(user: UserInterface) {
            try {
                const response = await api.put<ApiResponse<UserInterface>>('/users/update', user);
                this._auth = response.data.data;
            } catch (err: AxiosError | any) {
                doError(err);
            }
        },
        async socialLogin(provider:string, id_token: string, profile?: object) {
            try {
                const response = await api.post<ApiResponse<UserInterface>>('/login/' + provider, {
                    id_token: id_token,
                    profile: profile
                });
                resetSessionData(); checkedAt=Date.now(); this._list=null;
                this._auth = response.data.data;
                const token: string|undefined = response.data.data?.token;
                if (token) {
                    localStorage.setItem('token', token);
                    api.defaults.headers.Authorization = `Bearer ${token}`;
                }
            } catch (err: AxiosError | any) {
                doError(err);
            }
        },
        async reset(form:PasswordReset) {
            try {
              await api.post<ApiResponse<UserInterface>>('/password/email', form);
            } catch (err: AxiosError | any) {
                doError(err);
            }
    },
        async changePassword(form:PasswordChange) {
            try {
                await api.post<ApiResponse<UserInterface>>('/password/change', form);
            } catch (err: AxiosError | any) {
                doError(err);
            }
        },
        async deleteAccount() {
            try {
                await unregisterPushNotifications(false).catch(() => {});
                await api.delete<ApiResponse<UserInterface>>('/me');
                this.clearLocalSession();
            } catch (err: AxiosError | any) {
                doError(err);
            }
        }
    }
})
